/**
 * Свайп влево-вправо — соседний раздел (15.10.2026).
 *
 * Разделы «Денег» и «Аналитики» — вкладками вверху экрана, и на большом
 * телефоне до них приходилось тянуться большим пальцем. Теперь раздел
 * листается по всему экрану, как страницы.
 *
 * Свайп не перехватывается там, где у жеста своё дело: в листе поверх
 * экрана, в поле ввода, в ленте, которая сама листается вбок, у краёв экрана
 * (там системный «назад»). Короткое, медленное или скорее вертикальное
 * движение — это прокрутка, а не свайп.
 *
 * Раздел едет за пальцем (16.10.2026): отпустил дальше порога — уезжает, и
 * с другой стороны въезжает соседний; не дотянул — мягко возвращается. У
 * крайнего раздела — «резинка»: тянется, но меньше пальца. Без движения,
 * если в системе выбрано «Уменьшить движение».
 */
import { useEffect, useRef } from 'react';

/** Сколько пикселей пройти пальцем, чтобы это был свайп. */
const MIN_DX = 60;
/** Во сколько раз движение вбок должно быть больше, чем вниз. */
const SLOPE = 1.8;
/** Свайп — быстрый жест: дольше — человек что-то разглядывает. */
const MAX_MS = 700;
/** Полоса у края экрана — для системного жеста «назад». */
const EDGE = 24;
/** Сколько пройти, чтобы понять: палец ведёт вбок, а не листает вниз. */
const LOCK = 10;
/** Дотянул дальше этой доли экрана — раздел сменится и при медленном жесте. */
const FAR = 0.35;
/** Что едет за пальцем: содержимое раздела, без заголовка с вкладками. */
const MOVING = 'main > :is(section, .note), .ops > :not(.an-sections)';
const calm = (): boolean => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Сдвинуть содержимое раздела; `ms` — доехать плавно, без него — вслед за пальцем. */
function slide(x: number, ms = 0, fade = Math.min(0.4, Math.abs(x) / 600)): void {
  for (const el of document.querySelectorAll<HTMLElement>(MOVING)) {
    el.style.transition = ms ? `transform ${ms}ms cubic-bezier(.2,.8,.2,1), opacity ${ms}ms ease-out` : 'none';
    el.style.transform = x ? `translateX(${x}px)` : '';
    el.style.opacity = fade ? String(1 - fade) : '';
  }
}
function release(): void {
  for (const el of document.querySelectorAll<HTMLElement>(MOVING)) {
    el.style.transition = ''; el.style.transform = ''; el.style.opacity = '';
  }
}

export function useSwipeTabs<K extends string>(
  keys: readonly K[], current: K, onChange: (k: K) => void,
  opts: {
    enabled?: boolean;
    /**
     * Свайп только внутри этого листа — у записи операции. Без него — по
     * экрану, и тогда листы поверх свайп не отдают.
     */
    within?: string;
    /** Подсказка «листай свайпом» этого места: свайп по ней — тоже свайп. */
    coach?: 'sections' | 'optabs';
  } = {},
): void {
  const enabled = opts.enabled ?? true;
  const within = opts.within;
  const coach = opts.coach;
  const latest = useRef({ keys, current, onChange });
  latest.current = { keys, current, onChange };
  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;
    let start: { x: number; y: number; t: number; side: boolean | null } | null = null;
    /** Палец ведёт раздел за собой — только на экране, не в листе. */
    const follow = !within && !calm();
    const down = (e: TouchEvent) => {
      const t = e.touches[0];
      if (e.touches.length !== 1 || !t || t.clientX < EDGE || t.clientX > window.innerWidth - EDGE
        || ownGesture(e.target as Element | null, within, coach)) {
        start = null;
        return;
      }
      start = { x: t.clientX, y: t.clientY, t: Date.now(), side: null };
    };
    const move = (e: TouchEvent) => {
      const s = start;
      const t = e.touches[0];
      if (!s || !t || !follow) return;
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      if (s.side === null) {
        if (Math.abs(dx) < LOCK && Math.abs(dy) < LOCK) return;
        // Скорее вниз — это прокрутка: раздел не трогаем до конца касания.
        s.side = Math.abs(dx) > Math.abs(dy) * 1.2;
        if (!s.side) return;
      }
      if (!s.side) return;
      const { keys: list, current: now } = latest.current;
      const edge = !list[list.indexOf(now) + (dx < 0 ? 1 : -1)];
      // У крайнего раздела — «резинка»: тянется, но втрое меньше пальца.
      slide(edge ? dx * 0.3 : dx);
    };
    const up = (e: TouchEvent) => {
      const s = start;
      start = null;
      const t = e.changedTouches[0];
      if (!s || !t) return;
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      const { keys: list, current: now, onChange: go } = latest.current;
      const next = list[list.indexOf(now) + (dx < 0 ? 1 : -1)];
      const quick = Math.abs(dx) >= MIN_DX && Math.abs(dx) >= Math.abs(dy) * SLOPE && Date.now() - s.t <= MAX_MS;
      // Медленно, но далеко — тоже смена раздела: палец довёл его сам.
      const far = follow && s.side === true && Math.abs(dx) > window.innerWidth * FAR;
      if (!next || !(quick || far)) {
        // Не дотянул — раздел возвращается на место.
        if (follow && s.side) { slide(0, 220, 0); window.setTimeout(release, 240); }
        return;
      }
      const switchTo = () => {
        // Новый раздел въезжает с той стороны, откуда его потянули. Свайп в
        // листе страницу под ним не трогает.
        if (!within) {
          const root = document.documentElement;
          root.dataset.swipe = dx < 0 ? 'next' : 'prev';
          window.setTimeout(() => { delete root.dataset.swipe; }, 320);
        }
        go(next);
      };
      if (follow && s.side) {
        // Уезжает в ту сторону, куда тянули, и только потом сменяется.
        slide((dx < 0 ? -1 : 1) * window.innerWidth * 0.45, 150, 0.9);
        window.setTimeout(() => { release(); switchTo(); }, 140);
        return;
      }
      switchTo();
    };
    const cancel = () => {
      if (start?.side && follow) { slide(0, 220, 0); window.setTimeout(release, 240); }
      start = null;
    };
    document.addEventListener('touchstart', down, { passive: true });
    document.addEventListener('touchmove', move, { passive: true });
    document.addEventListener('touchend', up, { passive: true });
    document.addEventListener('touchcancel', cancel, { passive: true });
    return () => {
      document.removeEventListener('touchstart', down);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', up);
      document.removeEventListener('touchcancel', cancel);
    };
  }, [enabled, within, coach]);
}

/** У жеста здесь своё дело: лист поверх экрана, поле, ползунок или лента, листаемая вбок. */
function ownGesture(el: Element | null, within?: string, coach?: string): boolean {
  // Свайп по подсказке листает то, чему она учит, — и только это.
  const hint = el?.closest('.coach');
  if (hint) return hint.getAttribute('data-for') !== coach;
  // Поле, где набирают текст, ведёт пальцем каретку. Прозрачный выбор даты
  // или времени поверх строки (`.opover`) открывается тапом — свайп ему не мешает.
  if (!el || el.closest('textarea, input:not(.opover), select:not(.opover), [role="slider"]')) return true;
  // Строка операции листается сама: свайп открывает её кнопки.
  if (el.closest('.ops-swipe')) return true;
  // Карточка счёта, цели или долга в записи листается сама — соседний вариант;
  // ряд видов операции прокручивается, когда не влез (27.10.2026).
  if (el.closest('.opcar, .optype')) return true;
  const scope = within ? el.closest(within) : null;
  // Свой лист — свайп его; другой лист поверх — нет.
  if (within ? !scope || el.closest('.sheet') !== scope.closest('.sheet') : el.closest('.sheet')) return true;
  for (let n: Element | null = el; n && n !== scope && n !== document.body; n = n.parentElement) {
    const h = n as HTMLElement;
    if (h.scrollWidth > h.clientWidth + 2) {
      const ox = getComputedStyle(h).overflowX;
      if (ox === 'auto' || ox === 'scroll') return true;
    }
  }
  return false;
}
